import { TestRunDetailService } from '@/api/testRunDetail';
import { Loading } from '@/components/Loading';
import { StatusHistoryTestRun } from '@/components/result/StatusHistoryTestRun';
import { Section } from '@/components/Section';
import { CardDetail } from '@/components/shared/card/CardDetail';
import { Tab, TabGroup, TabList, TabPanel, TabPanels } from '@headlessui/react';
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { ListIssueTestRun } from '@/pages/testIssue/ListIssueTestRun';
import { StepDetail } from '@/components/step/StepDetail';
import { formatDate } from '@/utils/libs/dateUtil';
import { useBackNavigate } from '@/utils/hooks/useBackNavigate';
import { CreateResult } from '@/pages/result/CreateResult';
import { Button } from 'alurkerja-ui';
import { useProtectedRoute } from '@/routes/ProtectedRouteContext';

type OverviewTestRunDetailProps = {
  id?: string;
  backAction?: () => void;
  onSuccess?: () => void;
  toClose?: () => void;
  setIsErrorDetail?: (isError: boolean) => void;
};

export const OverviewTestRunDetail: React.FC<OverviewTestRunDetailProps> = ({
  id,
  backAction,
  onSuccess,
  toClose,
  setIsErrorDetail = () => {
  },
}) => {
  const { isPublic } = useProtectedRoute();
  const { testRunDetailId } = useParams();
  const backNavigate = useBackNavigate(isPublic);
  const toBack = () => (backAction ? backAction() : backNavigate());

  const {
    data: testRunDetailData,
    isFetching,
    isError: isErrorTestRunDetailData,
  } = TestRunDetailService.useGet(id ?? testRunDetailId!);

  const [activeTab, setActiveTab] = useState<number>(0);
  const [saveAction, setSaveAction] = useState<(() => void) | undefined>(
    undefined,
  );
  const [isSaving, setIsSaving] = useState<boolean>(false);

  useEffect(() => {
    setIsErrorDetail(isErrorTestRunDetailData);
  }, [isErrorTestRunDetailData, setIsErrorDetail]);

  if (isFetching) {
    return (
      <Section>
        <Loading />
      </Section>
    );
  }

  return (
    <TabGroup className="tab-group" onChange={index => setActiveTab(index)}>
      <TabList className="tab-list">
        <Tab className="tab-button">Test Case</Tab>
        <Tab className="tab-button">Step</Tab>
        <Tab className="tab-button">Status History</Tab>
        <Tab className="tab-button">Issue</Tab>
        {!isPublic && (
          <Tab className="tab-button">
            Add Result
          </Tab>
        )}
      </TabList>

      <Section
        title={`Case Code - [${testRunDetailData?.test_case?.case_code}]`}
      >
        <TabPanels>
          <TabPanel>
            <Section title={`Title - ${testRunDetailData?.test_case?.title}`}>
              <div className="flex flex-col gap-4">
                <div className="flex flex-row gap-6">
                  <CardDetail
                    title="Type"
                    value={testRunDetailData?.test_case?.type_label}
                  />
                  <CardDetail
                    title="Module"
                    value={testRunDetailData?.test_case?.module?.module_name}
                  />
                </div>
                <div className="flex flex-row gap-6">
                  <CardDetail
                    title="Priority"
                    value={testRunDetailData?.test_case?.priority_label}
                  />
                  <CardDetail
                    title="Tags"
                    value={testRunDetailData?.test_case?.tag_label}
                  />
                </div>
                <div className="flex flex-row gap-6">
                  <CardDetail
                    title="Created By"
                    value={testRunDetailData?.test_case?.created_by?.full_name}
                  />
                  <CardDetail
                    title="Created At"
                    value={formatDate(testRunDetailData?.test_case?.created_at)}
                  />
                </div>
                <div className="flex flex-row gap-6">
                  <CardDetail
                    title="Updated By"
                    value={formatDate(
                      testRunDetailData?.test_case?.updated_by?.full_name,
                    )}
                  />
                  <CardDetail
                    title="Updated At"
                    value={formatDate(testRunDetailData?.test_case?.updated_at)}
                  />
                </div>
                <div className="flex flex-row gap-6">
                  <CardDetail
                    title="Description"
                    value={testRunDetailData?.test_case?.description}
                  />
                  <CardDetail
                    title="Precondition"
                    value={testRunDetailData?.test_case?.precondition}
                  />
                </div>
              </div>
            </Section>
          </TabPanel>

          <TabPanel>
            <Section title={`Title - ${testRunDetailData?.test_case?.title}`}>
              {testRunDetailData?.test_case && (
                <StepDetail testCase={testRunDetailData!.test_case!} />
              )}
            </Section>
          </TabPanel>

          <TabPanel>
            <Section title={`Title - ${testRunDetailData?.test_case?.title}`}>
              <StatusHistoryTestRun testRun={testRunDetailData!} />
            </Section>
          </TabPanel>

          <TabPanel>
            <ListIssueTestRun
              testRun={testRunDetailData!}
              toClose={toClose}
            />
          </TabPanel>

          {!isPublic && (
            <TabPanel>
              <Section title={`Title - ${testRunDetailData?.test_case?.title}`}>
                <CreateResult
                  testRun={testRunDetailData!}
                  backAction={toBack}
                  onSuccess={onSuccess}
                  setSaveAction={setSaveAction}
                  setIsSaving={setIsSaving}
                />
              </Section>
            </TabPanel>
          )}
        </TabPanels>
      </Section>

      <div className="p-3 mx-4 mt-0 bg-white rounded-md shadow-md flex items-center justify-between">
        {activeTab === 4 && saveAction && !isPublic && (
          <Button onClick={saveAction} loading={isSaving} className="ml-auto">
            Save
          </Button>
        )}
      </div>
    </TabGroup>
  );
};
